import { defineRelations, sql } from "drizzle-orm";
import {
  index,
  integer,
  sqliteTable,
  text,
  uniqueIndex,
} from "drizzle-orm/sqlite-core";
import type { AdminPermission } from "@/lib/permissions";

export const user = sqliteTable(
  "user",
  {
    id: text("id").primaryKey(),
    name: text("name").notNull(),
    email: text("email").notNull().unique(),
    emailVerified: integer("email_verified", { mode: "boolean" })
      .default(false)
      .notNull(),
    image: text("image"),
    createdAt: integer("created_at", { mode: "timestamp_ms" })
      .default(sql`(cast(unixepoch('subsecond') * 1000 as integer))`)
      .notNull(),
    updatedAt: integer("updated_at", { mode: "timestamp_ms" })
      .default(sql`(cast(unixepoch('subsecond') * 1000 as integer))`)
      .$onUpdate(() => /* @__PURE__ */ new Date())
      .notNull(),
    username: text("username").unique(),
    displayUsername: text("display_username"),
    phoneNumber: text("phone_number").unique(),
    phoneNumberVerified: integer("phone_number_verified", { mode: "boolean" })
      .default(false)
      .notNull(),
    role: text("role"),
    banned: integer("banned", { mode: "boolean" }).default(false),
    banReason: text("ban_reason"),
    banExpires: integer("ban_expires", { mode: "timestamp_ms" }),
    // Per-user admin-panel permission override for restricted (admin_staff) users.
    // null = use the role's baseline (mirrors queen.permissions for moderators).
    adminPermissions: text("admin_permissions", {
      mode: "json",
    }).$type<AdminPermission | null>(),
    pausedAt: integer("paused_at", { mode: "timestamp_ms" }),

    // ── Application contact email ───────────────────────────────────────────────
    // `email` above belongs to Better Auth. It is a REQUIRED, unique identity
    // column, so a phone-only signup still needs *something* in it — Better Auth
    // fills it with a random value at a non-routable `.invalid` domain. It is
    // therefore not a mailbox and must never be displayed, searched, exported or
    // mailed. `contactEmail` is the app's own delivery address: nullable, so
    // "this account has no email" is representable in the type system instead of
    // being detected at runtime, and meaningful only once
    // `contactEmailVerifiedAt` is non-null. See `plans/phone-auth-launch-audit.md`
    // EMAIL-01.
    contactEmail: text("contact_email"),
    contactEmailVerifiedAt: integer("contact_email_verified_at", {
      mode: "timestamp_ms",
    }),
    // Where the address came from: an OAuth provider, the person typing it, or
    // Apple's private relay (which needs its own outbound-domain registration
    // before SES can reach it at all).
    contactEmailSource: text("contact_email_source").$type<
      "oauth" | "user" | "apple_relay" | null
    >(),
    // Set when someone declines the email ask, so the prompt stops nagging.
    contactEmailPromptDismissedAt: integer(
      "contact_email_prompt_dismissed_at",
      {
        mode: "timestamp_ms",
      },
    ),
    // Set when someone dismisses the "bring your ChatGPT here" tips in chats
    // and comment composers; cleared from /settings/assistant to show them again.
    assistantTipsDismissedAt: integer("assistant_tips_dismissed_at", {
      mode: "timestamp_ms",
    }),
    // "Hiive bot for me" switched off (a chat's Hiive bot page or
    // /settings/assistant): they can't tag it, it is vetoed in their direct
    // chats and on their posts, and it never reads what they write.
    assistantChatsBlockedAt: integer("assistant_chats_blocked_at", {
      mode: "timestamp_ms",
    }),
    // A permanent bounce or complaint clears `contactEmailVerifiedAt` and records
    // why here, so the ticket page can re-offer the ask with an honest reason.
    contactEmailLastFailureAt: integer("contact_email_last_failure_at", {
      mode: "timestamp_ms",
    }),
    contactEmailLastFailureReason: text(
      "contact_email_last_failure_reason",
    ).$type<"hard_bounce" | "complaint" | null>(),

    // ── Credential recency ──────────────────────────────────────────────────────
    // Stamped after successful phone verification (including attachment from a
    // fresh OAuth session). It measures phone-credential recency, not general
    // account activity: using Google last week does not prove that a recycled
    // SIM is still controlled by the same person. A dormant phone credential
    // blocks rather than guessing. See AUTH-08.
    lastAuthenticatedAt: integer("last_authenticated_at", {
      mode: "timestamp_ms",
    }),

    // ── Referral capture ────────────────────────────────────────────────────────
    // Stamped by the provider-neutral finalizer when it creates the one-time
    // profile boundary. The code rides in the OAuth callback URL, so Apple's
    // cross-site form_post does not depend on a SameSite cookie being sent.
    // This column is the durable store until onboarding completion consumes it.
    pendingReferralCode: text("pending_referral_code"),
    pendingReferralSource: text("pending_referral_source"),
    pendingReferralAt: integer("pending_referral_at", { mode: "timestamp_ms" }),

    // ── Account consolidation tombstone ───────────────────────────────────────
    // Merged users are retained as non-login tombstones rather than deleted.
    // Historical audit/actor references can therefore keep pointing at the
    // identity that performed an action, while every user-owned record is moved
    // to the surviving account. This is intentionally a soft reference: a
    // self-referencing FK makes SQLite table rebuilds and account recovery much
    // harder without adding protection that the merge transaction does not
    // already enforce.
    mergedIntoUserId: text("merged_into_user_id"),
    mergedAt: integer("merged_at", { mode: "timestamp_ms" }),
  },
  (table) => [
    index("user_contact_email_idx").on(table.contactEmail),
    index("user_merged_into_idx").on(table.mergedIntoUserId),
  ],
);

export const session = sqliteTable(
  "session",
  {
    id: text("id").primaryKey(),
    expiresAt: integer("expires_at", { mode: "timestamp_ms" }).notNull(),
    token: text("token").notNull().unique(),
    createdAt: integer("created_at", { mode: "timestamp_ms" })
      .default(sql`(cast(unixepoch('subsecond') * 1000 as integer))`)
      .notNull(),
    updatedAt: integer("updated_at", { mode: "timestamp_ms" })
      .$onUpdate(() => /* @__PURE__ */ new Date())
      .notNull(),
    ipAddress: text("ip_address"),
    userAgent: text("user_agent"),
    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),
    impersonatedBy: text("impersonated_by"),
  },
  (table) => [index("session_userId_idx").on(table.userId)],
);

export const account = sqliteTable(
  "account",
  {
    id: text("id").primaryKey(),
    accountId: text("account_id").notNull(),
    // better-auth 1.7.0-1.7.2 keyed an identity on (issuer, accountId) and
    // required an `issuer` column here. 1.7.3 reverted to the 1.6 schema and
    // matches on (providerId, accountId) again, and the core stopped writing
    // `issuer` — a NOT NULL column nothing populates rejected every sign-up
    // and account link. Do not reintroduce it without checking the installed
    // major: see 20260911000000_better_auth_revert_account_issuer.
    providerId: text("provider_id").notNull(),
    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),
    accessToken: text("access_token"),
    refreshToken: text("refresh_token"),
    idToken: text("id_token"),
    accessTokenExpiresAt: integer("access_token_expires_at", {
      mode: "timestamp_ms",
    }),
    refreshTokenExpiresAt: integer("refresh_token_expires_at", {
      mode: "timestamp_ms",
    }),
    scope: text("scope"),
    password: text("password"),
    createdAt: integer("created_at", { mode: "timestamp_ms" })
      .default(sql`(cast(unixepoch('subsecond') * 1000 as integer))`)
      .notNull(),
    updatedAt: integer("updated_at", { mode: "timestamp_ms" })
      .$onUpdate(() => /* @__PURE__ */ new Date())
      .notNull(),
  },
  (table) => [index("account_userId_idx").on(table.userId)],
);

export const verification = sqliteTable(
  "verification",
  {
    id: text("id").primaryKey(),
    identifier: text("identifier").notNull(),
    value: text("value").notNull(),
    expiresAt: integer("expires_at", { mode: "timestamp_ms" }).notNull(),
    createdAt: integer("created_at", { mode: "timestamp_ms" })
      .default(sql`(cast(unixepoch('subsecond') * 1000 as integer))`)
      .notNull(),
    updatedAt: integer("updated_at", { mode: "timestamp_ms" })
      .default(sql`(cast(unixepoch('subsecond') * 1000 as integer))`)
      .$onUpdate(() => /* @__PURE__ */ new Date())
      .notNull(),
  },
  (table) => [index("verification_identifier_idx").on(table.identifier)],
);

/**
 * Candidate contact emails awaiting verification.
 *
 * A candidate lives HERE, never in `user.contactEmail`, so a typo or a hostile
 * input cannot displace an address that already works and cannot receive a
 * ticket. The code is stored as a keyed HMAC, not a bare hash: a six-digit
 * space is small enough to enumerate offline from an unkeyed digest.
 */
export const contactEmailVerification = sqliteTable(
  "contact_email_verification",
  {
    id: text("id").primaryKey(),
    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),
    /** Normalized (trimmed, lowercased) candidate address. */
    candidateEmail: text("candidate_email").notNull(),
    /** Why we are collecting it — drives the copy and the follow-up action. */
    purpose: text("purpose")
      .$type<"contact_email">()
      .notNull()
      .default("contact_email"),
    /** Free-form origin label, e.g. "event_checkout" or "wallet_ticket". */
    context: text("context"),
    /**
     * Event booking this verification should mail once it succeeds. A soft
     * reference on purpose: a deleted booking must not block the person from
     * verifying their address.
     */
    eventBookingId: integer("event_booking_id"),
    codeHash: text("code_hash").notNull(),
    expiresAt: integer("expires_at", { mode: "timestamp_ms" }).notNull(),
    attempts: integer("attempts").notNull().default(0),
    maxAttempts: integer("max_attempts").notNull().default(5),
    consumedAt: integer("consumed_at", { mode: "timestamp_ms" }),
    /** SES message id of the verification mail, for webhook correlation. */
    providerMessageId: text("provider_message_id"),
    createdAt: integer("created_at", { mode: "timestamp_ms" })
      .default(sql`(cast(unixepoch('subsecond') * 1000 as integer))`)
      .notNull(),
    updatedAt: integer("updated_at", { mode: "timestamp_ms" })
      .default(sql`(cast(unixepoch('subsecond') * 1000 as integer))`)
      .$onUpdate(() => /* @__PURE__ */ new Date())
      .notNull(),
  },
  (table) => [
    uniqueIndex("contact_email_verification_one_live_user_unique")
      .on(table.userId)
      .where(sql`${table.consumedAt} IS NULL`),
    index("contact_email_verification_user_idx").on(
      table.userId,
      table.consumedAt,
    ),
    index("contact_email_verification_candidate_idx").on(table.candidateEmail),
    index("contact_email_verification_expires_idx").on(table.expiresAt),
  ],
);

/**
 * Append-only audit of phone-credential changes. The OTP itself is never
 * recorded, and the number is stored only as a keyed hash — enough to correlate
 * an incident, not enough to leak a contact list if the table is read.
 */
export const phoneCredentialAudit = sqliteTable(
  "phone_credential_audit",
  {
    id: text("id").primaryKey(),
    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),
    action: text("action")
      .$type<
        | "signup"
        | "signin"
        | "attached"
        | "changed"
        | "cleared"
        | "collision_blocked"
        | "dormant_blocked"
        | "merge_started"
        | "merged_in"
        | "merged_out"
      >()
      .notNull(),
    phoneHash: text("phone_hash").notNull(),
    previousPhoneHash: text("previous_phone_hash"),
    actor: text("actor").$type<"self" | "system" | "admin">().notNull(),
    ipHash: text("ip_hash"),
    userAgent: text("user_agent"),
    metadata: text("metadata", { mode: "json" }).$type<Record<
      string,
      unknown
    > | null>(),
    createdAt: integer("created_at", { mode: "timestamp_ms" })
      .default(sql`(cast(unixepoch('subsecond') * 1000 as integer))`)
      .notNull(),
  },
  (table) => [
    index("phone_credential_audit_user_idx").on(table.userId, table.createdAt),
  ],
);

/**
 * Short-lived proof that the signed-in account also completed the OTP for a
 * number owned by another account.
 *
 * The raw phone number never lives here. The intent is bound to both user IDs,
 * the current session is re-checked on every mutation, and the stored preview
 * hash makes a confirmation fail closed if either account changes after the
 * review screen was rendered.
 */
export const accountMergeIntent = sqliteTable(
  "account_merge_intent",
  {
    id: text("id").primaryKey(),
    primaryUserId: text("primary_user_id")
      .notNull()
      .references(() => user.id, { onDelete: "restrict" }),
    secondaryUserId: text("secondary_user_id")
      .notNull()
      .references(() => user.id, { onDelete: "restrict" }),
    phoneHash: text("phone_hash").notNull(),
    state: text("state")
      .$type<
        | "preparing"
        | "pending"
        | "consumed"
        | "cancelled"
        | "expired"
        | "failed"
      >()
      .notNull()
      .default("pending"),
    previewHash: text("preview_hash").notNull(),
    /** Hash of exact rows and values; unlike previewHash it never reaches UI. */
    manifestHash: text("manifest_hash"),
    preview: text("preview", { mode: "json" })
      .$type<Record<string, unknown>>()
      .notNull(),
    decisions: text("decisions", { mode: "json" }).$type<Record<
      string,
      unknown
    > | null>(),
    failureCode: text("failure_code"),
    expiresAt: integer("expires_at", { mode: "timestamp_ms" }).notNull(),
    consumedAt: integer("consumed_at", { mode: "timestamp_ms" }),
    createdAt: integer("created_at", { mode: "timestamp_ms" })
      .default(sql`(cast(unixepoch('subsecond') * 1000 as integer))`)
      .notNull(),
    updatedAt: integer("updated_at", { mode: "timestamp_ms" })
      .default(sql`(cast(unixepoch('subsecond') * 1000 as integer))`)
      .$onUpdate(() => /* @__PURE__ */ new Date())
      .notNull(),
  },
  (table) => [
    index("account_merge_intent_primary_state_idx").on(
      table.primaryUserId,
      table.state,
      table.expiresAt,
    ),
    index("account_merge_intent_secondary_state_idx").on(
      table.secondaryUserId,
      table.state,
      table.expiresAt,
    ),
  ],
);

/** Immutable evidence for a completed account consolidation. */
export const accountMergeAudit = sqliteTable(
  "account_merge_audit",
  {
    id: text("id").primaryKey(),
    intentId: text("intent_id")
      .notNull()
      .references(() => accountMergeIntent.id, { onDelete: "restrict" }),
    primaryUserId: text("primary_user_id")
      .notNull()
      .references(() => user.id, { onDelete: "restrict" }),
    secondaryUserId: text("secondary_user_id")
      .notNull()
      .references(() => user.id, { onDelete: "restrict" }),
    phoneHash: text("phone_hash").notNull(),
    previewHash: text("preview_hash").notNull(),
    manifestHash: text("manifest_hash"),
    result: text("result", { mode: "json" })
      .$type<Record<string, unknown>>()
      .notNull(),
    ipHash: text("ip_hash"),
    userAgent: text("user_agent"),
    createdAt: integer("created_at", { mode: "timestamp_ms" })
      .default(sql`(cast(unixepoch('subsecond') * 1000 as integer))`)
      .notNull(),
  },
  (table) => [
    uniqueIndex("account_merge_audit_intent_unique").on(table.intentId),
    index("account_merge_audit_primary_idx").on(
      table.primaryUserId,
      table.createdAt,
    ),
    index("account_merge_audit_secondary_idx").on(
      table.secondaryUserId,
      table.createdAt,
    ),
  ],
);

/** Human-review queue for merges that intentionally fail closed. */
export const accountMergeCase = sqliteTable(
  "account_merge_case",
  {
    id: text("id").primaryKey(),
    intentId: text("intent_id")
      .notNull()
      .references(() => accountMergeIntent.id, { onDelete: "restrict" }),
    primaryUserId: text("primary_user_id")
      .notNull()
      .references(() => user.id, { onDelete: "restrict" }),
    secondaryUserId: text("secondary_user_id")
      .notNull()
      .references(() => user.id, { onDelete: "restrict" }),
    status: text("status")
      .$type<"open" | "resolved" | "closed">()
      .notNull()
      .default("open"),
    reasons: text("reasons", { mode: "json" }).$type<string[]>().notNull(),
    reviewedByUserId: text("reviewed_by_user_id").references(() => user.id, {
      onDelete: "set null",
    }),
    resolution: text("resolution"),
    resolvedAt: integer("resolved_at", { mode: "timestamp_ms" }),
    createdAt: integer("created_at", { mode: "timestamp_ms" })
      .default(sql`(cast(unixepoch('subsecond') * 1000 as integer))`)
      .notNull(),
    updatedAt: integer("updated_at", { mode: "timestamp_ms" })
      .default(sql`(cast(unixepoch('subsecond') * 1000 as integer))`)
      .$onUpdate(() => /* @__PURE__ */ new Date())
      .notNull(),
  },
  (table) => [
    uniqueIndex("account_merge_case_intent_unique").on(table.intentId),
    index("account_merge_case_status_idx").on(table.status, table.createdAt),
  ],
);

/** Durable repair work created in the same transaction as a successful merge. */
export const accountMergeRepairJob = sqliteTable(
  "account_merge_repair_job",
  {
    id: text("id").primaryKey(),
    intentId: text("intent_id")
      .notNull()
      .references(() => accountMergeIntent.id, { onDelete: "restrict" }),
    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "restrict" }),
    kind: text("kind")
      .$type<"phone_claims" | "weight_personal_records">()
      .notNull(),
    state: text("state")
      .$type<"pending" | "running" | "completed" | "failed">()
      .notNull()
      .default("pending"),
    attempts: integer("attempts").notNull().default(0),
    lastError: text("last_error"),
    completedAt: integer("completed_at", { mode: "timestamp_ms" }),
    createdAt: integer("created_at", { mode: "timestamp_ms" })
      .default(sql`(cast(unixepoch('subsecond') * 1000 as integer))`)
      .notNull(),
    updatedAt: integer("updated_at", { mode: "timestamp_ms" })
      .default(sql`(cast(unixepoch('subsecond') * 1000 as integer))`)
      .$onUpdate(() => /* @__PURE__ */ new Date())
      .notNull(),
  },
  (table) => [
    uniqueIndex("account_merge_repair_job_intent_kind_unique").on(
      table.intentId,
      table.kind,
    ),
    index("account_merge_repair_job_state_idx").on(
      table.state,
      table.createdAt,
    ),
  ],
);

/** Append-only lifecycle trail, including blocked and failed attempts. */
export const accountMergeEvent = sqliteTable(
  "account_merge_event",
  {
    id: text("id").primaryKey(),
    intentId: text("intent_id").references(() => accountMergeIntent.id, {
      onDelete: "restrict",
    }),
    primaryUserId: text("primary_user_id")
      .notNull()
      .references(() => user.id, { onDelete: "restrict" }),
    secondaryUserId: text("secondary_user_id").references(() => user.id, {
      onDelete: "restrict",
    }),
    event: text("event")
      .$type<
        | "otp_verified"
        | "preview_ready"
        | "preview_failed"
        | "blocked"
        | "confirm_failed"
        | "confirmed"
        | "cancelled"
        | "case_resolved"
        | "repair_completed"
        | "repair_failed"
      >()
      .notNull(),
    metadata: text("metadata", { mode: "json" }).$type<Record<
      string,
      unknown
    > | null>(),
    createdAt: integer("created_at", { mode: "timestamp_ms" })
      .default(sql`(cast(unixepoch('subsecond') * 1000 as integer))`)
      .notNull(),
  },
  (table) => [
    index("account_merge_event_intent_idx").on(table.intentId, table.createdAt),
    index("account_merge_event_primary_idx").on(
      table.primaryUserId,
      table.createdAt,
    ),
  ],
);

export const jwks = sqliteTable("jwks", {
  id: text("id").primaryKey(),
  publicKey: text("public_key").notNull(),
  privateKey: text("private_key").notNull(),
  createdAt: integer("created_at", { mode: "timestamp_ms" }).notNull(),
  expiresAt: integer("expires_at", { mode: "timestamp_ms" }),
  alg: text("alg"),
  crv: text("crv"),
});

export const oauthClient = sqliteTable(
  "oauth_client",
  {
    id: text("id").primaryKey(),
    clientId: text("client_id").notNull().unique(),
    clientSecret: text("client_secret"),
    clientDiscoveryId: text("client_discovery_id"),
    disabled: integer("disabled", { mode: "boolean" }).default(false),
    skipConsent: integer("skip_consent", { mode: "boolean" }),
    enableEndSession: integer("enable_end_session", { mode: "boolean" }),
    subjectType: text("subject_type"),
    scopes: text("scopes", { mode: "json" }),
    clientCredentialsScopes: text("client_credentials_scopes", {
      mode: "json",
    }),
    userId: text("user_id").references(() => user.id, { onDelete: "cascade" }),
    createdAt: integer("created_at", { mode: "timestamp_ms" }),
    updatedAt: integer("updated_at", { mode: "timestamp_ms" }),
    name: text("name"),
    uri: text("uri"),
    icon: text("icon"),
    contacts: text("contacts", { mode: "json" }),
    tos: text("tos"),
    policy: text("policy"),
    softwareId: text("software_id"),
    softwareVersion: text("software_version"),
    softwareStatement: text("software_statement"),
    redirectUris: text("redirect_uris", { mode: "json" }).notNull(),
    postLogoutRedirectUris: text("post_logout_redirect_uris", { mode: "json" }),
    backchannelLogoutUri: text("backchannel_logout_uri"),
    backchannelLogoutSessionRequired: integer(
      "backchannel_logout_session_required",
      { mode: "boolean" },
    ),
    tokenEndpointAuthMethod: text("token_endpoint_auth_method"),
    applicationType: text("application_type"),
    jwks: text("jwks"),
    jwksUri: text("jwks_uri"),
    grantTypes: text("grant_types", { mode: "json" }),
    responseTypes: text("response_types", { mode: "json" }),
    requirePKCE: integer("require_pkce", { mode: "boolean" }),
    dpopBoundAccessTokens: integer("dpop_bound_access_tokens", {
      mode: "boolean",
    }).default(false),
    referenceId: text("reference_id"),
    metadata: text("metadata", { mode: "json" }),
  },
  (table) => [index("oauthClient_userId_idx").on(table.userId)],
);

export const oauthResource = sqliteTable("oauth_resource", {
  id: text("id").primaryKey(),
  identifier: text("identifier").notNull().unique(),
  name: text("name").notNull(),
  accessTokenTtl: integer("access_token_ttl"),
  refreshTokenTtl: integer("refresh_token_ttl"),
  signingAlgorithm: text("signing_algorithm"),
  signingKeyId: text("signing_key_id"),
  allowedScopes: text("allowed_scopes", { mode: "json" }),
  customClaims: text("custom_claims", { mode: "json" }),
  dpopBoundAccessTokensRequired: integer("dpop_bound_access_tokens_required", {
    mode: "boolean",
  }).default(false),
  disabled: integer("disabled", { mode: "boolean" }).default(false),
  createdAt: integer("created_at", { mode: "timestamp_ms" }),
  updatedAt: integer("updated_at", { mode: "timestamp_ms" }),
  policyVersion: integer("policy_version").default(1),
  metadata: text("metadata", { mode: "json" }),
});

export const oauthClientResource = sqliteTable(
  "oauth_client_resource",
  {
    id: text("id").primaryKey(),
    clientId: text("client_id")
      .notNull()
      .references(() => oauthClient.clientId, { onDelete: "cascade" }),
    resourceId: text("resource_id")
      .notNull()
      .references(() => oauthResource.identifier, { onDelete: "cascade" }),
    metadata: text("metadata", { mode: "json" }),
    createdAt: integer("created_at", { mode: "timestamp_ms" }),
  },
  (table) => [
    index("oauthClientResource_clientId_idx").on(table.clientId),
    index("oauthClientResource_resourceId_idx").on(table.resourceId),
  ],
);

export const oauthRefreshToken = sqliteTable(
  "oauth_refresh_token",
  {
    id: text("id").primaryKey(),
    token: text("token").notNull().unique(),
    clientId: text("client_id")
      .notNull()
      .references(() => oauthClient.clientId, { onDelete: "cascade" }),
    sessionId: text("session_id").references(() => session.id, {
      onDelete: "set null",
    }),
    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),
    referenceId: text("reference_id"),
    authorizationCodeId: text("authorization_code_id"),
    resources: text("resources", { mode: "json" }),
    requestedUserInfoClaims: text("requested_user_info_claims", {
      mode: "json",
    }),
    expiresAt: integer("expires_at", { mode: "timestamp_ms" }),
    createdAt: integer("created_at", { mode: "timestamp_ms" }),
    revoked: integer("revoked", { mode: "timestamp_ms" }),
    rotatedAt: integer("rotated_at", { mode: "timestamp_ms" }),
    rotationReplayResponse: text("rotation_replay_response"),
    rotationReplayExpiresAt: integer("rotation_replay_expires_at", {
      mode: "timestamp_ms",
    }),
    authTime: integer("auth_time", { mode: "timestamp_ms" }),
    confirmation: text("confirmation", { mode: "json" }),
    scopes: text("scopes", { mode: "json" }).notNull(),
  },
  (table) => [
    index("oauthRefreshToken_clientId_idx").on(table.clientId),
    index("oauthRefreshToken_sessionId_idx").on(table.sessionId),
    index("oauthRefreshToken_userId_idx").on(table.userId),
    index("oauthRefreshToken_authorizationCodeId_idx").on(
      table.authorizationCodeId,
    ),
  ],
);

export const oauthAccessToken = sqliteTable(
  "oauth_access_token",
  {
    id: text("id").primaryKey(),
    token: text("token").unique(),
    clientId: text("client_id")
      .notNull()
      .references(() => oauthClient.clientId, { onDelete: "cascade" }),
    sessionId: text("session_id").references(() => session.id, {
      onDelete: "set null",
    }),
    userId: text("user_id").references(() => user.id, { onDelete: "cascade" }),
    referenceId: text("reference_id"),
    authorizationCodeId: text("authorization_code_id"),
    resources: text("resources", { mode: "json" }),
    requestedUserInfoClaims: text("requested_user_info_claims", {
      mode: "json",
    }),
    refreshId: text("refresh_id").references(() => oauthRefreshToken.id, {
      onDelete: "cascade",
    }),
    expiresAt: integer("expires_at", { mode: "timestamp_ms" }),
    createdAt: integer("created_at", { mode: "timestamp_ms" }),
    revoked: integer("revoked", { mode: "timestamp_ms" }),
    confirmation: text("confirmation", { mode: "json" }),
    scopes: text("scopes", { mode: "json" }).notNull(),
  },
  (table) => [
    index("oauthAccessToken_clientId_idx").on(table.clientId),
    index("oauthAccessToken_sessionId_idx").on(table.sessionId),
    index("oauthAccessToken_userId_idx").on(table.userId),
    index("oauthAccessToken_authorizationCodeId_idx").on(
      table.authorizationCodeId,
    ),
    index("oauthAccessToken_refreshId_idx").on(table.refreshId),
  ],
);

export const oauthConsent = sqliteTable(
  "oauth_consent",
  {
    id: text("id").primaryKey(),
    clientId: text("client_id")
      .notNull()
      .references(() => oauthClient.clientId, { onDelete: "cascade" }),
    userId: text("user_id").references(() => user.id, { onDelete: "cascade" }),
    referenceId: text("reference_id"),
    resources: text("resources", { mode: "json" }),
    requestedUserInfoClaims: text("requested_user_info_claims", {
      mode: "json",
    }),
    scopes: text("scopes", { mode: "json" }).notNull(),
    createdAt: integer("created_at", { mode: "timestamp_ms" }),
    updatedAt: integer("updated_at", { mode: "timestamp_ms" }),
  },
  (table) => [
    index("oauthConsent_clientId_idx").on(table.clientId),
    index("oauthConsent_userId_idx").on(table.userId),
  ],
);

export const oauthClientAssertion = sqliteTable("oauth_client_assertion", {
  id: text("id").primaryKey(),
  expiresAt: integer("expires_at", { mode: "timestamp_ms" }).notNull(),
});

export const relations = defineRelations(
  {
    user,
    session,
    account,
    verification,
    jwks,
    oauthClient,
    oauthResource,
    oauthClientResource,
    oauthRefreshToken,
    oauthAccessToken,
    oauthConsent,
    oauthClientAssertion,
  },
  (r) => ({
    user: {
      sessions: r.many.session({
        from: r.user.id,
        to: r.session.userId,
      }),
      accounts: r.many.account({
        from: r.user.id,
        to: r.account.userId,
      }),
      oauthClients: r.many.oauthClient({
        from: r.user.id,
        to: r.oauthClient.userId,
      }),
      oauthRefreshTokens: r.many.oauthRefreshToken({
        from: r.user.id,
        to: r.oauthRefreshToken.userId,
      }),
      oauthAccessTokens: r.many.oauthAccessToken({
        from: r.user.id,
        to: r.oauthAccessToken.userId,
      }),
      oauthConsents: r.many.oauthConsent({
        from: r.user.id,
        to: r.oauthConsent.userId,
      }),
    },
    session: {
      user: r.one.user({
        from: r.session.userId,
        to: r.user.id,
      }),
      oauthRefreshTokens: r.many.oauthRefreshToken({
        from: r.session.id,
        to: r.oauthRefreshToken.sessionId,
      }),
      oauthAccessTokens: r.many.oauthAccessToken({
        from: r.session.id,
        to: r.oauthAccessToken.sessionId,
      }),
    },
    account: {
      user: r.one.user({
        from: r.account.userId,
        to: r.user.id,
      }),
    },
    oauthClient: {
      user: r.one.user({ from: r.oauthClient.userId, to: r.user.id }),
      resources: r.many.oauthClientResource({
        from: r.oauthClient.clientId,
        to: r.oauthClientResource.clientId,
      }),
      refreshTokens: r.many.oauthRefreshToken({
        from: r.oauthClient.clientId,
        to: r.oauthRefreshToken.clientId,
      }),
      accessTokens: r.many.oauthAccessToken({
        from: r.oauthClient.clientId,
        to: r.oauthAccessToken.clientId,
      }),
      consents: r.many.oauthConsent({
        from: r.oauthClient.clientId,
        to: r.oauthConsent.clientId,
      }),
    },
    oauthResource: {
      clients: r.many.oauthClientResource({
        from: r.oauthResource.identifier,
        to: r.oauthClientResource.resourceId,
      }),
    },
    oauthClientResource: {
      client: r.one.oauthClient({
        from: r.oauthClientResource.clientId,
        to: r.oauthClient.clientId,
      }),
      resource: r.one.oauthResource({
        from: r.oauthClientResource.resourceId,
        to: r.oauthResource.identifier,
      }),
    },
    oauthRefreshToken: {
      client: r.one.oauthClient({
        from: r.oauthRefreshToken.clientId,
        to: r.oauthClient.clientId,
      }),
      session: r.one.session({
        from: r.oauthRefreshToken.sessionId,
        to: r.session.id,
      }),
      user: r.one.user({ from: r.oauthRefreshToken.userId, to: r.user.id }),
      accessTokens: r.many.oauthAccessToken({
        from: r.oauthRefreshToken.id,
        to: r.oauthAccessToken.refreshId,
      }),
    },
    oauthAccessToken: {
      client: r.one.oauthClient({
        from: r.oauthAccessToken.clientId,
        to: r.oauthClient.clientId,
      }),
      session: r.one.session({
        from: r.oauthAccessToken.sessionId,
        to: r.session.id,
      }),
      user: r.one.user({ from: r.oauthAccessToken.userId, to: r.user.id }),
      refreshToken: r.one.oauthRefreshToken({
        from: r.oauthAccessToken.refreshId,
        to: r.oauthRefreshToken.id,
      }),
    },
    oauthConsent: {
      client: r.one.oauthClient({
        from: r.oauthConsent.clientId,
        to: r.oauthClient.clientId,
      }),
      user: r.one.user({ from: r.oauthConsent.userId, to: r.user.id }),
    },
  }),
);
